import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = ["#0284C7", "#EA580C", "#059669", "#7C3AED", "#DB2777", "#D97706"];
const TOOLTIP_STYLE = { background: "#FFFFFF", border: "1px solid #E2E8F0", fontSize: 12, borderRadius: 4, color: "#0F172A" };

function Panel({ title, testId, children, empty }) {
  return (
    <div data-testid={testId} className="bg-card border border-border rounded-md p-4">
      <h3 className="font-heading uppercase font-semibold text-xs text-muted-foreground mb-3">{title}</h3>
      {empty ? <p className="text-xs text-muted-foreground py-10 text-center">Pas encore de données.</p> : children}
    </div>
  );
}

export default function DashboardCharts() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`).then((res) => setStats(res.data)).catch(() => setError(true));
  }, []);

  if (error) {
    return <p data-testid="dashboard-charts-error" className="text-xs text-muted-foreground mb-10">Impossible de charger les statistiques pour le moment.</p>;
  }
  if (!stats) {
    return <div data-testid="dashboard-charts-loading" className="grid sm:grid-cols-2 gap-4 mb-10">
      {[0, 1, 2, 3].map((i) => <div key={i} className="bg-card border border-border rounded-md p-4 h-64 animate-pulse" />)}
    </div>;
  }

  return (
    <div className="grid sm:grid-cols-2 gap-4 mb-10" data-testid="dashboard-charts">
      <Panel title="Analyses par statut" testId="chart-status-breakdown" empty={stats.status_breakdown.length === 0}>
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={stats.status_breakdown} dataKey="count" nameKey="statut" cx="50%" cy="50%" outerRadius={80} label={({ statut, count }) => `${statut} (${count})`}>
              {stats.status_breakdown.map((_, i) => <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} />
          </PieChart>
        </ResponsiveContainer>
      </Panel>

      <Panel title="Actions HFIX ouvertes / fermées" testId="chart-actions-summary" empty={stats.actions_summary.ouvertes + stats.actions_summary.fermees === 0}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={[{ name: "Actions", Ouvertes: stats.actions_summary.ouvertes, Fermées: stats.actions_summary.fermees }]}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
            <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Bar dataKey="Ouvertes" fill="#EA580C" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Fermées" fill="#059669" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <Panel title="Top facteurs HFACS retenus" testId="chart-top-factors" empty={stats.top_factors.length === 0}>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={stats.top_factors} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis type="number" stroke="#64748B" fontSize={11} allowDecimals={false} />
            <YAxis type="category" dataKey="facteur" stroke="#64748B" fontSize={10} width={140} tickFormatter={(v) => (v?.length > 22 ? `${v.slice(0, 22)}…` : v)} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Bar dataKey="count" fill="#0284C7" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <Panel title="Évolution mensuelle des analyses" testId="chart-monthly-evolution" empty={stats.monthly_evolution.length === 0}>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={stats.monthly_evolution}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="mois" stroke="#64748B" fontSize={11} />
            <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Line type="monotone" dataKey="count" stroke="#EA580C" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}
